import { ref, computed, toValue, watch } from 'vue'
import type { MaybeRefOrGetter } from 'vue'

export interface PaginatedFetchOptions {
  /** Total row count backing `totalPages` - typically a computed reading from a Vuex store */
  totalCount: MaybeRefOrGetter<number>
  /** Fetches one page of rows for the given top/skip/signal. `skipCount` is forwarded from
   * `fetch({ skipCount })` for actions that bundle the count into the same request instead
   * of using a separate `fetchCount` - ignore it if this action doesn't need it. */
  fetchRows: (params: { top: number, skip: number, signal: AbortSignal, skipCount: boolean }) => Promise<void>
  /** Fetches the total count for the current filters. Kept separate from `fetchRows` so plain
   * page navigation can skip recomputing it via `fetch({ skipCount: true })`. Omit this when
   * `fetchRows` already bundles the count fetch itself, gated on the `skipCount` param it receives. */
  fetchCount?: (params: { signal: AbortSignal }) => Promise<void>
  /** Identifies a superseded-request cancellation so it's swallowed rather than thrown.
   * Defaults to recognizing native AbortController and axios cancellations. */
  isCancelled?: (error: unknown) => boolean
  /** Reactive flag mirroring the underlying store action's own "already fetching" guard,
   * for actions that silently drop a call made while one is already in flight instead of
   * queuing it (rather than honoring the AbortSignal `fetchRows`/`fetchCount` receive).
   * When provided, `fetch()` checks this before calling `fetchRows`/`fetchCount` and, if
   * it's already true, defers instead of firing a call the store would just drop - then
   * automatically replays once it goes false, so a fetch requested mid-flight (e.g. a
   * rapid page click) isn't lost. */
  isFetching?: MaybeRefOrGetter<boolean>
  pageSizeOptions?: number[]
  defaultPageSize?: number
  /** localStorage key for persisting the chosen page size. Omit to skip persistence. */
  pageSizeStorageKey?: string
}

function defaultIsCancelled(error: unknown): boolean {
  const { name, code } = (error ?? {}) as { name?: string, code?: string }
  return name === 'AbortError' || name === 'CanceledError' || code === 'ERR_CANCELED'
}

function readStoredPageSize(key: string, options: number[], fallback: number): number {
  try {
    const saved = localStorage.getItem(key)
    const parsed = saved ? Number.parseInt(saved, 10) : NaN
    return options.includes(parsed) ? parsed : fallback
  }
  catch (error) {
    console.error(error)
    return fallback
  }
}

/**
 * Shared page-state + fetch-cancellation logic for AM3DataList screens migrating from
 * infinite scroll to page-number pagination: currentPage/pageSize state (with optional
 * localStorage persistence), a totalPages computed, and a rows+count fetch pair guarded
 * by a self-cancelling AbortController so a superseded request (rapid page/filter/search
 * changes) can never land after a newer one and show stale data.
 *
 * Store-agnostic by design: `fetchRows`/`fetchCount` are caller-provided closures over
 * whatever Vuex action the screen uses, so this composable never dispatches directly.
 */
export function usePaginatedFetch(options: PaginatedFetchOptions) {
  const {
    totalCount,
    fetchRows,
    fetchCount,
    isFetching,
    isCancelled = defaultIsCancelled,
    pageSizeOptions = [25, 50, 100],
    defaultPageSize = pageSizeOptions[0] ?? 50,
    pageSizeStorageKey,
  } = options

  const currentPage = ref(1)
  const pageSize = ref(
    pageSizeStorageKey
      ? readStoredPageSize(pageSizeStorageKey, pageSizeOptions, defaultPageSize)
      : defaultPageSize,
  )

  const totalPages = computed(() => Math.max(1, Math.ceil((toValue(totalCount) || 0) / pageSize.value)))

  let pendingAbortController: AbortController | null = null
  let deferredFetchOptions: { skipCount?: boolean } | null = null

  if (isFetching) {
    watch(() => toValue(isFetching), (fetching) => {
      if (!fetching && deferredFetchOptions) {
        const fetchOptions = deferredFetchOptions
        deferredFetchOptions = null
        fetch(fetchOptions)
      }
    })
  }

  async function fetch(fetchOptions: { skipCount?: boolean } = {}): Promise<void> {
    if (isFetching && toValue(isFetching)) {
      deferredFetchOptions = fetchOptions
      return
    }

    pendingAbortController?.abort()
    const abortController = new AbortController()
    pendingAbortController = abortController
    const { signal } = abortController
    const skipCount = fetchOptions.skipCount ?? false

    try {
      const requests = [fetchRows({ top: pageSize.value, skip: (currentPage.value - 1) * pageSize.value, signal, skipCount })]
      if (fetchCount && !skipCount) {
        requests.push(fetchCount({ signal }))
      }
      await Promise.all(requests)
    }
    catch (error) {
      if (isCancelled(error)) return
      throw error
    }
    finally {
      if (pendingAbortController === abortController) {
        pendingAbortController = null
      }
    }
  }

  function resetToFirstPage(): void {
    currentPage.value = 1
  }

  async function handlePageChange(page: number): Promise<void> {
    currentPage.value = page
    await fetch({ skipCount: true })
  }

  async function handlePageSizeChange(size: number): Promise<void> {
    pageSize.value = size
    currentPage.value = 1
    if (pageSizeStorageKey) {
      try {
        localStorage.setItem(pageSizeStorageKey, String(size))
      }
      catch (error) {
        console.error(error)
      }
    }
    await fetch()
  }

  return {
    currentPage,
    pageSize,
    totalPages,
    fetch,
    resetToFirstPage,
    handlePageChange,
    handlePageSizeChange,
  }
}
